import type { ComplianceFormValues } from './validation';

const STORAGE_PREFIX = 'compliance-form-draft';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface ComplianceDraftPayload {
  values: ComplianceFormValues;
  fileName: string | null;
  ocrPreviewTaskId: string | null;
  updatedAt: number;
}

const buildKey = (key: string) => `${STORAGE_PREFIX}:${key}`;

const isValidDraft = (payload: unknown): payload is ComplianceDraftPayload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const draft = payload as ComplianceDraftPayload;
  if (!draft.values || typeof draft.updatedAt !== 'number') {
    return false;
  }
  return true;
};

export const setComplianceDraft = (
  key: string,
  payload: Omit<ComplianceDraftPayload, 'updatedAt'>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const stored: ComplianceDraftPayload = {
    ...payload,
    updatedAt: Date.now(),
  };
  window.sessionStorage.setItem(buildKey(key), JSON.stringify(stored));
};

export const getComplianceDraft = (
  key: string,
): ComplianceDraftPayload | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = window.sessionStorage.getItem(buildKey(key));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!isValidDraft(parsed)) {
      return null;
    }
    if (Date.now() - parsed.updatedAt > DRAFT_TTL_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearComplianceDraft = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(buildKey(key));
};
